import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faPen, faBan, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import ProtocoloFilterBar from '../../components/ProtocoloFilterBar';
import AnularProtocoloModal from './AnularProtocoloModal';
import ProtocoloCards from './ProtocoloCards';
import SequenciaProtocoloPanel from './SequenciaProtocoloPanel';
import protocoloService from '../../services/protocoloService';
import { extensaoService } from '../../services/extensaoService';
import cursoService from '../../services/cursoService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Listagem do protocolo de requerimento — o que antes era o livro de papel.
 *
 * Ordenada do número mais alto para o mais baixo porque a secretaria quase
 * sempre procura o que acabou de entrar.
 */

const FILTROS_VAZIOS = {
  numero: '',
  requerente: '',
  tipo: '',
  curso: '',
  extensao: '',
  status: '',
  dataInicio: '',
  dataFim: ''
};

const formatarData = (valor) => {
  if (!valor) return '-';
  const [ano, mes, dia] = String(valor).slice(0, 10).split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : '-';
};

const ProtocolosList = () => {
  const { hasPermission } = useAuth();

  const [protocolos, setProtocolos] = useState([]);
  const [paginacao, setPaginacao] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filtros, setFiltros] = useState(FILTROS_VAZIOS);
  const [filtrosAplicados, setFiltrosAplicados] = useState(FILTROS_VAZIOS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [tipos, setTipos] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [extensoes, setExtensoes] = useState([]);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [protocoloParaAnular, setProtocoloParaAnular] = useState(null);
  const [exportando, setExportando] = useState(false);

  const podeAnular = hasPermission('protocolos.anular');
  const podeEditar = hasPermission('protocolos.update');

  useEffect(() => {
    const carregarApoio = async () => {
      try {
        const [respTipos, respCursos, respExtensoes] = await Promise.all([
          protocoloService.listarTipos(),
          cursoService.list({ page: 1, limit: 1000 }),
          extensaoService.list({ page: 1, limit: 1000 })
        ]);
        setTipos(respTipos.data || []);
        setCursos(respCursos.data || []);
        setExtensoes(respExtensoes.data || []);
      } catch (err) {
        console.error('Erro ao carregar dados de apoio dos protocolos:', err);
      }
    };

    carregarApoio();
  }, []);

  const carregarProtocolos = useCallback(async () => {
    try {
      setCarregando(true);
      setErro('');

      const resposta = await protocoloService.list({
        ...filtrosAplicados,
        page,
        limit: pageSize
      });

      setProtocolos(resposta.data || []);
      setPaginacao(resposta.pagination || { page, limit: pageSize, total: 0, totalPages: 1 });
    } catch (err) {
      setErro(err.message || 'Não foi possível carregar os protocolos.');
      setProtocolos([]);
    } finally {
      setCarregando(false);
    }
  }, [filtrosAplicados, page, pageSize]);

  useEffect(() => {
    carregarProtocolos();
  }, [carregarProtocolos]);

  const handleFiltroChange = (e) => {
    setFiltros(anterior => ({ ...anterior, [e.target.name]: e.target.value }));
  };

  const aplicarFiltros = () => {
    setPage(1);
    setFiltrosAplicados(filtros);
  };

  const limparFiltros = () => {
    setFiltros(FILTROS_VAZIOS);
    setFiltrosAplicados(FILTROS_VAZIOS);
    setPage(1);
  };

  const confirmarAnulacao = async (motivo) => {
    const resposta = await protocoloService.anular(protocoloParaAnular.id, motivo);
    toast.success(resposta.message || 'Protocolo anulado.');
    setProtocoloParaAnular(null);
    carregarProtocolos();
  };

  // A dica do estado vazio nao pode sugerir uma acao que o usuario nao tem:
  // quem so enxerga a listagem nao consegue registrar protocolo.
  const dicaVazio = hasPermission('protocolos.create')
    ? 'Revise os filtros aplicados ou registre um novo protocolo.'
    : 'Revise os filtros aplicados.';

  const rotuloTipo = (valor) => tipos.find(t => t.valor === valor)?.rotulo || valor;

  /**
   * Exporta o resultado do filtro inteiro, não só a página na tela.
   *
   * Os dados saem da mesma rota que a listagem usa, e é isso que garante o
   * recorte: a API aplica a permissão e filtra pelas extensões do usuário.
   * Montar a planilha a partir de outra fonte abriria um caminho paralelo,
   * sem as duas proteções.
   */
  const TETO_EXPORTACAO = 20000;

  const exportarExcel = async () => {
    if (exportando) return;
    setExportando(true);

    try {
      const porLote = 1000;
      const linhas = [];
      let pagina = 1;
      let totalPaginas = 1;

      do {
        const resposta = await protocoloService.list({
          ...filtrosAplicados,
          page: pagina,
          limit: porLote
        });

        linhas.push(...(resposta.data || []));
        totalPaginas = resposta.pagination?.totalPages || 1;
        pagina += 1;
      } while (pagina <= totalPaginas && linhas.length < TETO_EXPORTACAO);

      if (linhas.length === 0) {
        toast.info('Não há protocolos para exportar com os filtros aplicados.');
        return;
      }

      const planilha = linhas.map(p => ({
        'Nº': p.numero,
        'Requerente': p.requerente_nome,
        'Tipo': rotuloTipo(p.tipo_requerimento),
        'Curso': p.cursoNome || '',
        'Extensão': p.extensaoNome || '',
        'Entrada': formatarData(p.data_entrada),
        'Origem': p.origem === 'AUTOMATICO' ? `Pedido ${p.pedido ?? ''}`.trim() : 'Manual',
        'Situação': p.status === 'ANULADO' ? 'Anulado' : 'Ativo',
        'Motivo da anulação': p.motivo_anulacao || '',
        'Anulado por': p.anulado_por || '',
        'Registrado por': p.cadastrado_por || ''
      }));

      const aba = XLSX.utils.json_to_sheet(planilha);
      const arquivo = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(arquivo, aba, 'Protocolos');
      XLSX.writeFile(arquivo, `protocolos-${new Date().toISOString().slice(0, 10)}.xlsx`);

      if (linhas.length >= TETO_EXPORTACAO) {
        toast.warning(
          `A exportação foi limitada a ${TETO_EXPORTACAO.toLocaleString('pt-BR')} protocolos. ` +
          'Reduza o período ou filtre por extensão para exportar o restante.'
        );
      } else {
        toast.success(`${linhas.length} protocolo(s) exportado(s).`);
      }
    } catch (err) {
      toast.error(err.message || 'Não foi possível exportar os protocolos.');
    } finally {
      setExportando(false);
    }
  };

  const colunas = [
    {
      accessor: 'numero',
      Header: 'Nº',
      Cell: (valor, linha) => (
        <span className={linha.status === 'ANULADO' ? 'line-through text-gray-400' : 'font-semibold'}>
          {valor}
        </span>
      )
    },
    { accessor: 'requerente_nome', Header: 'Requerente' },
    { accessor: 'tipo_requerimento', Header: 'Tipo', Cell: (valor) => rotuloTipo(valor) },
    { accessor: 'cursoNome', Header: 'Curso', Cell: (valor) => valor || '-' },
    { accessor: 'extensaoNome', Header: 'Extensão', Cell: (valor) => valor || '-' },
    { accessor: 'data_entrada', Header: 'Entrada', Cell: formatarData },
    {
      accessor: 'origem',
      Header: 'Origem',
      Cell: (valor, linha) =>
        valor === 'AUTOMATICO' ? (
          <span title={`Gerado pela baixa do pedido ${linha.pedido ?? ''}`}>Pedido</span>
        ) : (
          'Manual'
        )
    },
    {
      accessor: 'status',
      Header: 'Situação',
      Cell: (valor, linha) =>
        valor === 'ANULADO' ? (
          <span
            data-testid="situacao-protocolo"
            className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700"
            title={linha.motivo_anulacao || 'Protocolo anulado'}
          >
            Anulado
          </span>
        ) : (
          <span
            data-testid="situacao-protocolo"
            className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700"
          >
            Ativo
          </span>
        )
    },
    {
      accessor: 'acoes',
      Header: 'Ações',
      Cell: (_valor, linha) => {
        if (linha.status === 'ANULADO') {
          return <span className="text-sm text-gray-400">—</span>;
        }

        return (
          <div className="flex items-center gap-3">
            {podeEditar && (
              <Link
                to={`/protocolos/${linha.id}/edit`}
                className="text-green-600 hover:text-green-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-green-400 inline-flex items-center gap-1"
                title={`Editar protocolo ${linha.numero}`}
              >
                <FontAwesomeIcon icon={faPen} />
                <span className="text-sm">Editar</span>
              </Link>
            )}
            {podeAnular && (
              <button
                type="button"
                onClick={() => setProtocoloParaAnular(linha)}
                className="text-red-600 hover:text-red-800 p-1 rounded focus:outline-none focus:ring-2 focus:ring-red-400 inline-flex items-center gap-1"
                title={`Anular protocolo ${linha.numero}`}
              >
                <FontAwesomeIcon icon={faBan} />
                <span className="text-sm">Anular</span>
              </button>
            )}
          </div>
        );
      }
    }
  ];

  if (!hasPermission('protocolos.list')) {
    return (
      <div className="flex-1 w-full max-w-full overflow-x-hidden">
        <div className="px-2 sm:px-6">
          <div className="flex justify-center items-center h-64">
            <div className="text-red-600">
              Acesso negado: você não tem permissão para visualizar os protocolos.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full max-w-full overflow-x-hidden">
      <div className="px-2 sm:px-6">
        <div className="max-w-full">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
              Protocolos de Requerimento
            </h2>
            <div className="flex flex-col sm:flex-row gap-2">
            {hasPermission('protocolos.exportar') && (
              <button
                type="button"
                onClick={exportarExcel}
                disabled={exportando}
                className="min-h-11 px-4 py-2 rounded bg-green-700 text-white font-semibold shadow hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faFileExcel} className={exportando ? 'animate-pulse' : ''} />
                <span>{exportando ? 'Exportando...' : 'Exportar Excel'}</span>
              </button>
            )}
            {hasPermission('protocolos.create') && (
              <Link
                to="/protocolos/novo"
                className="self-start sm:self-auto min-h-11 px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faPlus} />
                <span>Registrar protocolo</span>
              </Link>
            )}
            </div>
          </div>

          <SequenciaProtocoloPanel />

          <ProtocoloFilterBar
            filtros={filtros}
            onChange={handleFiltroChange}
            onAplicar={aplicarFiltros}
            onLimpar={limparFiltros}
            tipos={tipos}
            cursos={cursos}
            extensoes={extensoes}
          />

          {erro && (
            <div className="mb-4 px-4 py-3 rounded bg-red-50 border border-red-200 text-red-700">
              {erro}
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
            <div className="order-2 sm:order-1 w-full sm:w-auto min-w-0">
              <Pagination
                currentPage={paginacao.page}
                totalPages={paginacao.totalPages}
                totalItems={paginacao.total}
                itemsPerPage={paginacao.limit}
                onPageChange={setPage}
              />
            </div>
            <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
              <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap">
                Por página:
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                className="min-h-11 px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-400"
              >
                {[10, 20, 50, 100].map(tamanho => (
                  <option key={tamanho} value={tamanho}>{tamanho}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Celular: cartão. A tabela só cabe a partir de sm, e ali ela rola
              dentro do container sem esconder nada essencial. */}
          <div className="sm:hidden max-w-full">
            <ProtocoloCards
              protocolos={protocolos}
              carregando={carregando}
              rotuloTipo={rotuloTipo}
              formatarData={formatarData}
              podeEditar={podeEditar}
              podeAnular={podeAnular}
              dicaVazio={dicaVazio}
              onAnular={setProtocoloParaAnular}
            />
          </div>

          <div className="hidden sm:block max-w-full">
            <Table
              columns={colunas}
              data={protocolos}
              loading={carregando}
              emptyMessage="Nenhum protocolo encontrado."
              emptyHint={dicaVazio}
            />
          </div>

          {!carregando && paginacao.total > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              Mostrando {((paginacao.page - 1) * paginacao.limit) + 1} a{' '}
              {Math.min(paginacao.page * paginacao.limit, paginacao.total)} de {paginacao.total} protocolos
            </div>
          )}
        </div>
      </div>

      {protocoloParaAnular && (
        <AnularProtocoloModal
          protocolo={protocoloParaAnular}
          onConfirmar={confirmarAnulacao}
          onFechar={() => setProtocoloParaAnular(null)}
        />
      )}
    </div>
  );
};

export default ProtocolosList;
